import React, { useEffect, useState } from 'react';
import { 
  Table, Button, Input, Space, Typography, Tag, 
  message, Popconfirm, Switch, Card, Modal, Select, Avatar 
} from 'antd';
import { 
  UserAddOutlined, DeleteOutlined, SettingOutlined, 
  UserOutlined, CheckCircleOutlined, StopOutlined 
} from '@ant-design/icons';
import { API_BASE } from '../config/api';

const { Title } = Typography;
const { Option } = Select;

const AdminEmployees = () => {
  const [employees, setEmployees] = useState([]);
  const [services, setServices] = useState([]); // Para el selector de asignación
  const [loading, setLoading] = useState(true);
  
  // Estados para creación
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newName, setNewName] = useState('');
  
  // Estados para asignar servicios
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [empRes, servRes] = await Promise.all([
        fetch(`${API_BASE}/employees`),
        fetch(`${API_BASE}/services`) // Asumiendo que esta es tu ruta de servicios
      ]);
      const empData = await empRes.json();
      const servData = await servRes.json();
      
      setEmployees(empData);
      setServices(servData);
    } catch (error) {
      message.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- ACCIONES ---

  const handleCreate = async () => {
    if (!newName) return message.warning("Ingresa un nombre");
    try {
      const res = await fetch(`${API_BASE}/employees`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName })
      });
      if (res.ok) {
        message.success("Empleado creado");
        setNewName('');
        setIsAddModalOpen(false);
        fetchData();
      }
    } catch (err) {
      message.error("Error al crear");
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/employees/${id}/toggle`, { method: "PATCH" });
      if (res.ok) {
        message.success("Estado actualizado");
        fetchData();
      }
    } catch (err) {
      message.error("Error al cambiar estado");
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/employees/${id}`, { method: "DELETE" });
      if (res.ok) {
        message.success("Empleado eliminado");
        fetchData();
      }
    } catch (err) {
      message.error("Error al eliminar");
    }
  };

  const handleAssignServices = async () => {
    try {
      const res = await fetch(`${API_BASE}/employees/${selectedEmployee.id}/services`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ serviceIds: selectedServices })
      });
      if (res.ok) {
        message.success("Servicios asignados");
        setIsAssignModalOpen(false);
        setSelectedServices([]);
        fetchData();
      }
    } catch (err) {
      message.error("Error al asignar");
    }
  };

  // --- COLUMNAS DE LA TABLA ---

  const columns = [
    {
      title: 'Empleado',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Avatar 
            style={{ backgroundColor: record.background_color }} 
            icon={<UserOutlined />} 
          />
          <b style={{ color: record.active ? '#000' : '#bfbfbf' }}>{text}</b>
        </Space>
      )
    },
    {
      title: 'Servicios Asignados',
      dataIndex: 'services',
      key: 'services',
      render: (services) => (
        <>
          {services?.map(s => (
            <Tag color="blue" key={s.id} style={{ marginBottom: '4px' }}>
              {s.name}
            </Tag>
          ))}
          {(!services || services.length === 0) && <Tag color="default">Sin servicios</Tag>}
        </>
      )
    },
    {
      title: 'Estado',
      dataIndex: 'active',
      key: 'active',
      width: 100,
      render: (active, record) => (
        <Switch 
          checked={active} 
          onChange={() => handleToggle(record.id)} 
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<StopOutlined />}
        />
      )
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button 
            icon={<SettingOutlined />} 
            onClick={() => {
              setSelectedEmployee(record);
              setSelectedServices(record.services?.map(s => s.id) || []);
              setIsAssignModalOpen(true);
            }}
          />
          <Popconfirm
            title="¿Eliminar empleado?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space style={{ marginBottom: 24, justifyContent: 'space-between', width: '100%' }}>
          <Title level={3} style={{ margin: 0 }}>Gestión de Empleados</Title>
          <Button 
            type="primary" 
            icon={<UserAddOutlined />} 
            onClick={() => setIsAddModalOpen(true)}
          >
            Nuevo Empleado
          </Button>
        </Space>

        <Table 
          dataSource={employees} 
          columns={columns} 
          rowKey="id" 
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* MODAL CREAR */}
      <Modal
        title="Agregar Nuevo Empleado"
        open={isAddModalOpen}
        onOk={handleCreate}
        onCancel={() => setIsAddModalOpen(false)}
        okText="Crear"
      >
        <Input 
          placeholder="Nombre completo" 
          value={newName} 
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={handleCreate}
        />
        <p style={{ marginTop: 10, color: '#8c8c8c' }}>
          Se le asignará un color aleatorio automáticamente.
        </p>
      </Modal>

      {/* MODAL ASIGNAR SERVICIOS */}
      <Modal
        title={`Asignar Servicios a: ${selectedEmployee?.name}`}
        open={isAssignModalOpen}
        onOk={handleAssignServices}
        onCancel={() => setIsAssignModalOpen(false)}
        okText="Guardar Cambios"
        width={600}
      >
        <p>Selecciona los servicios que este empleado puede realizar:</p>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Seleccionar servicios"
          value={selectedServices}
          onChange={(values) => setSelectedServices(values)}
          optionFilterProp="children"
        >
          {services.map(s => (
            <Option key={s.id} value={s.id}>{s.name}</Option>
          ))}
        </Select>
      </Modal>
    </div>
  );
};

export default AdminEmployees;
